<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import InboxChannelGlyph from 'dashboard/routes/dashboard/settings/inbox/components/InboxChannelGlyph.vue';
import ContactTeamsSection from 'dashboard/components-next/Contacts/ContactTeamsSection.vue';
import Policy from 'dashboard/components/policy.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'viewDetails']);

const { t } = useI18n();

const dialCodeCountries = computed(() => {
  return [...countries]
    .filter(country => country.dial_code)
    .sort((first, second) => second.dial_code.length - first.dial_code.length);
});

const country = computed(() => {
  const phoneNumberWithDialCode = props.contact.phoneNumber?.replace(
    /[^\d+]/g,
    ''
  );
  const normalizedPhoneNumber = phoneNumberWithDialCode
    ? `${phoneNumberWithDialCode.startsWith('+') ? '' : '+'}${phoneNumberWithDialCode}`
    : '';
  const phoneCountry =
    phoneNumberWithDialCode &&
    dialCodeCountries.value.find(countryItem =>
      normalizedPhoneNumber.startsWith(countryItem.dial_code)
    );
  return phoneCountry || null;
});

const lifecycleStage = computed(() => {
  return props.contact.lifecycleStage || props.contact.lifecycle_stage;
});

const labels = computed(() => props.contact.labels || []);

const contactTeams = ref(props.contact.teams || []);

watch(
  () => props.contact.teams,
  teams => {
    contactTeams.value = teams || [];
  }
);

const contactTeamIds = computed(() => contactTeams.value.map(team => team.id));

const onTeamsUpdated = teams => {
  contactTeams.value = teams;
};

const contactInboxes = computed(() => {
  return props.contact.contactInboxes || props.contact.contact_inboxes || [];
});

const channelInboxes = computed(() =>
  contactInboxes.value.map(ci => ci.inbox).filter(Boolean)
);

const fieldRows = computed(() => [
  {
    key: 'email',
    label: t('CONTACTS_LAYOUT.TABLE.EMAIL'),
    value: props.contact.email,
  },
  {
    key: 'phone',
    label: t('CONTACTS_LAYOUT.TABLE.PHONE'),
    value: props.contact.phoneNumber,
  },
  {
    key: 'language',
    label: t('CONTACTS_LAYOUT.TABLE.LANGUAGE'),
    value: props.contact.additionalAttributes?.language,
  },
]);
</script>

<template>
  <aside
    class="sticky top-0 flex h-[calc(100vh-9rem)] max-h-full w-96 shrink-0 flex-col overflow-hidden border-s border-n-weak bg-n-surface-2"
  >
    <header
      class="flex items-center justify-between gap-3 border-b border-n-weak p-4"
    >
      <div class="flex min-w-0 items-center gap-3">
        <Avatar
          :name="contact.name"
          :src="contact.thumbnail"
          :size="40"
          :status="contact.availabilityStatus"
          hide-offline-status
          rounded-full
        />
        <div class="min-w-0">
          <h3 class="truncate text-base font-semibold text-n-slate-12">
            {{ contact.name || t('CONTACTS_LAYOUT.TABLE.NA') }}
          </h3>
          <p class="truncate text-sm text-n-slate-10">
            {{
              contact.email ||
              contact.phoneNumber ||
              t('CONTACTS_LAYOUT.TABLE.NA')
            }}
          </p>
        </div>
      </div>
      <Button
        icon="i-lucide-x"
        color="slate"
        variant="ghost"
        size="sm"
        @click="emit('close')"
      />
    </header>

    <div class="flex-1 space-y-5 overflow-y-auto p-4">
      <section class="space-y-3">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.PREVIEW.DETAILS') }}
        </h4>
        <dl class="space-y-3">
          <div v-for="row in fieldRows" :key="row.key" class="space-y-1">
            <dt class="text-xs font-medium uppercase text-n-slate-10">
              {{ row.label }}
            </dt>
            <dd class="break-words text-sm text-n-slate-12">
              {{ row.value || t('CONTACTS_LAYOUT.TABLE.NA') }}
            </dd>
          </div>

          <div class="space-y-1">
            <dt class="text-xs font-medium uppercase text-n-slate-10">
              {{ t('CONTACTS_LAYOUT.TABLE.COUNTRY') }}
            </dt>
            <dd class="text-sm text-n-slate-12">
              <span v-if="country" class="inline-flex items-center gap-2">
                <Flag :country="country.id" class="size-4" />
                <span>{{ country.name }}</span>
              </span>
              <span v-else>{{ t('CONTACTS_LAYOUT.TABLE.NA') }}</span>
            </dd>
          </div>
        </dl>
      </section>

      <section class="space-y-3">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.TABLE.CHANNELS') }}
        </h4>
        <div v-if="channelInboxes.length" class="flex flex-wrap gap-2">
          <span
            v-for="(inbox, idx) in channelInboxes"
            :key="inbox.id ?? `pv-ch-${idx}`"
            v-tooltip.top="inbox.name || $t('CONTACTS_LAYOUT.TABLE.NA')"
            class="inline-flex shrink-0 cursor-default"
          >
            <InboxChannelGlyph :inbox="inbox" size="sm" />
          </span>
        </div>
        <p v-else class="text-sm text-n-slate-10">
          {{ t('CONTACTS_LAYOUT.TABLE.NA') }}
        </p>
      </section>

      <section class="space-y-3">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.TABLE.LIFECYCLE') }}
        </h4>
        <span
          v-if="lifecycleStage"
          class="inline-flex max-w-full items-center gap-2 rounded-md bg-n-alpha-2 px-2 py-1 text-sm text-n-slate-11"
        >
          <span>{{ lifecycleStage.icon }}</span>
          <span class="truncate">{{ lifecycleStage.name }}</span>
        </span>
        <p v-else class="text-sm text-n-slate-10">
          {{ t('CONTACTS_LAYOUT.TABLE.NA') }}
        </p>
      </section>

      <Policy :permissions="['administrator']">
        <ContactTeamsSection
          :contact-id="contact.id"
          :initial-team-ids="contactTeamIds"
          @updated="onTeamsUpdated"
        />
      </Policy>

      <section class="space-y-3">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.TABLE.LABELS') }}
        </h4>
        <div v-if="labels.length" class="flex flex-wrap gap-2">
          <span
            v-for="label in labels"
            :key="label"
            class="rounded-md bg-n-alpha-2 px-2 py-1 text-sm text-n-slate-11"
          >
            {{ label }}
          </span>
        </div>
        <p v-else class="text-sm text-n-slate-10">
          {{ t('CONTACTS_LAYOUT.TABLE.NA') }}
        </p>
      </section>
    </div>

    <footer class="border-t border-n-weak p-4">
      <Button
        :label="t('CONTACTS_LAYOUT.PREVIEW.VIEW_DETAILS')"
        color="blue"
        class="w-full"
        @click="emit('viewDetails', contact.id)"
      />
    </footer>
  </aside>
</template>
